import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

/**
 * DateRangePicker
 * Seletor de período com atalhos pré-definidos (Hoje, Semana, Mês, etc.)
 * e opção de período personalizado.
 * 
 * Exemplo:
 *   <DateRangePicker 
 *       startDate={startDate}
 *       endDate={endDate}
 *       onChange={({ startDate, endDate }) => { setStartDate(startDate); setEndDate(endDate); }}
 *   />
 */

const PRESETS = [
    { label: "Hoje", getValue: () => { const d = new Date(); d.setHours(0, 0, 0, 0); return { startDate: d, endDate: new Date() }; } },
    { label: "Ontem", getValue: () => { const d = new Date(); d.setDate(d.getDate() - 1); d.setHours(0, 0, 0, 0); const e = new Date(d); e.setHours(23, 59, 59, 999); return { startDate: d, endDate: e }; } },
    { label: "7 dias", getValue: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 7); s.setHours(0, 0, 0, 0); return { startDate: s, endDate: e }; } },
    { label: "30 dias", getValue: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 30); s.setHours(0, 0, 0, 0); return { startDate: s, endDate: e }; } },
    { label: "Este mês", getValue: () => { const s = new Date(); s.setDate(1); s.setHours(0, 0, 0, 0); return { startDate: s, endDate: new Date() }; } },
    { label: "Mês anterior", getValue: () => { const s = new Date(); s.setMonth(s.getMonth() - 1); s.setDate(1); s.setHours(0, 0, 0, 0); const e = new Date(s.getFullYear(), s.getMonth() + 1, 0, 23, 59, 59, 999); return { startDate: s, endDate: e }; } }
];

const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("pt-BR");
};

const toInputDate = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const DateRangePicker = ({
    startDate,
    endDate,
    onChange,
    className = ""
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activePreset, setActivePreset] = useState(null);
    const ref = useRef(null);

    // Fechar ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handlePreset = (preset, index) => {
        const range = preset.getValue();
        setActivePreset(index);
        onChange(range);
        setIsOpen(false);
    };

    const handleCustomChange = (field, value) => {
        const date = new Date(value + "T00:00:00");
        if (field === "startDate") {
            onChange({ startDate: date, endDate: endDate || date });
        } else {
            date.setHours(23, 59, 59, 999);
            onChange({ startDate: startDate || date, endDate: date });
        }
    };

    const displayText = startDate && endDate
        ? `${formatDate(startDate)} — ${formatDate(endDate)}`
        : "Selecione o período";

    return (
        <div className={`position-relative ${className}`} ref={ref}>
            <button
                className="btn btn-outline-secondary d-flex align-items-center"
                onClick={() => setIsOpen(!isOpen)}
                type="button"
            >
                <i className="mdi mdi-calendar-range me-2"></i>
                <span style={{ fontSize: "13px" }}>{displayText}</span>
                <i className={`mdi mdi-chevron-${isOpen ? "up" : "down"} ms-2`}></i>
            </button>

            {isOpen && (
                <div
                    className="bg-white shadow rounded-3 p-3 position-absolute mt-1"
                    style={{
                        zIndex: 1050,
                        minWidth: "320px",
                        right: 0,
                        border: "1px solid #e9ecef"
                    }}
                >
                    {/* Atalhos */}
                    <div className="d-flex flex-wrap gap-1 mb-3">
                        {PRESETS.map((preset, idx) => (
                            <button
                                key={idx}
                                className={`btn btn-sm ${activePreset === idx ? "btn-primary" : "btn-outline-secondary"}`}
                                onClick={() => handlePreset(preset, idx)}
                                type="button"
                                style={{ fontSize: "12px" }}
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>

                    {/* Campos personalizados */}
                    <div className="row g-2">
                        <div className="col-6">
                            <label className="form-label" style={{ fontSize: "11px" }}>De</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={toInputDate(startDate)}
                                onChange={(e) => handleCustomChange("startDate", e.target.value)}
                            />
                        </div>
                        <div className="col-6">
                            <label className="form-label" style={{ fontSize: "11px" }}>Até</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={toInputDate(endDate)}
                                onChange={(e) => handleCustomChange("endDate", e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

DateRangePicker.propTypes = {
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string
};

export default DateRangePicker;
